import React from 'react';
import { Grid, Button, makeStyles, Theme } from '@material-ui/core';
import { Dictionary, uniq, compact } from 'lodash';
import { TimeSeries, Site } from '../../../models';
import { AnalysisChart } from './AnalysisChart';
import { useState } from 'react';
import { SecondaryButton } from '../../ui/Buttons';
import { ErrorMessage } from '../../ui/ErrorMessage';
import testIdentifiers from '../../../tests/identifiers';

import TagsSelector from './TagsSelector';
import AnalysisControlsHeader from './AnalysisControlsHeader';

import { useTimeSeriesDownloadCSV } from 'src/helpers/useDownloadCSV';
import NoCharts from './NoCharts';
import TimeSelector from './TimeSelector';
import { QUICK_RANGES } from './useTimeSelector';
import { View } from 'src/state/contexts/analysisPage';
import { useRanges } from './useRanges';
import { TimeRange } from 'src/components/ui/chart/types';
import { ChartZoomControl } from './ChartZoomControl';

const ZOOM_CONTROL_HEIGHT = 60;

export type TagsDrawerStatus = {
  isOpen: boolean;
  viewId: string | null;
  chartId: string | null;
};

export interface Props {
  site: Site;
  start: Date;
  end: Date;
  setStartDate: (start: Date) => void;
  setEndDate: (end: Date) => void;
  reset: () => void;
  snapshots: Dictionary<TimeSeries>;
  fullRangeSnapshots: Dictionary<TimeSeries>;
  loading: boolean;
  error?: string;
  lastUpdated: Date | null;
  views: View[];
  updateViews: (views: View[]) => void;
  updateTagsForChartInView: (viewId: string, chartId: string, tagsIds: string[]) => void;
  removeTagFromChartInView: (viewId: string, chartId: string, tagId: string) => void;
  removeChartInView: (viewId: string, chartId: string) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  pageContent: {
    height: '100%',
    flexWrap: 'nowrap',
    transition: theme.transitions.create('padding', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowY: 'auto',
  },
  mainContainer: {
    flex: '1',
    flexDirection: 'column',
    flexWrap: 'nowrap',
  },
  errorContainer: {
    padding: 36,
  },
  errorButton: {
    marginTop: 16,
  },
  headerContainer: {
    padding: 16,
    paddingRight: 16,
    margin: 0,
    width: 'auto',
  },
  siteInfoContainer: {
    padding: 16,
  },
  chartsContainer: {
    paddingBottom: `${ZOOM_CONTROL_HEIGHT + 16}px`,
  },
  chartWrapper: {
    padding: '16px 16px 0 16px',
    display: 'flex',
    gap: '16px',
    flexDirection: 'column',
  },
  zoomControlContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: '0 32px 8px 32px',
    background: 'white',
  },
  addChartButtonContainer: {
    padding: 16,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

export const AnalysisPage: React.FC<Props> = (props) => {
  const {
    site,
    start,
    end,
    snapshots,
    fullRangeSnapshots,
    views,
    setStartDate,
    setEndDate,
    reset,
    error,
    loading,
    lastUpdated,
    updateViews,
    updateTagsForChartInView,
    removeTagFromChartInView,
    removeChartInView,
  } = props;

  const [zoomRange, setZoomRange] = useState<TimeRange | undefined>(undefined);
  const tagIds = views.reduce<string[]>(
    (acc, view) => [
      ...acc,
      ...view.charts.reduce<string[]>((chartAcc, chart) => [...chartAcc, ...chart.tagsIds], []),
    ],
    []
  );
  const xRange = { start, end };

  const [triggeredOnly, setTriggeredOnly] = useState(false);

  const { fullRanges } = useRanges(tagIds, snapshots, xRange, zoomRange);
  const zoomFullRanges = [
    {
      yMin: 0,
      yMax: 1,
      xMin: xRange.start,
      xMax: xRange.end,
    },
  ];
  const [isTagsDrawerOpen, setIsTagsDrawerOpen] = useState<TagsDrawerStatus>({
    isOpen: false,
    viewId: null,
    chartId: null,
  });
  const [isTimeSelectorOpen, setIsTimeSelectorOpen] = useState<boolean>(false);
  const [selectedQuickRange, setSelectedQuickRange] = useState<
    (typeof QUICK_RANGES)[number]['key'] | undefined
  >(undefined);
  const classes = useStyles();

  const { downloading, download } = useTimeSeriesDownloadCSV(snapshots, start, end, site);

  const dateInputsDisabled = loading;

  const units = uniq(compact(tagIds.map((tagId) => snapshots[tagId]?.unit)));
  const maxUnits = 6;

  const handleAddChart = () => {
    setIsTagsDrawerOpen({ isOpen: true, viewId: null, chartId: null });
  };

  return (
    <div
      style={{
        position: 'relative',
        height: '100%',
      }}
    >
      <Grid
        data-testid={testIdentifiers.analysisPage.page}
        container
        className={classes.pageContent}
      >
        <Grid item container className={classes.mainContainer}>
          {error && (
            <Grid item container>
              <Grid item className={classes.errorContainer}>
                <ErrorMessage message={error} />
                <Button
                  data-testid={testIdentifiers.analysisPage.startOverButton}
                  className={classes.errorButton}
                  variant="contained"
                  onClick={() => reset()}
                >
                  Start Over
                </Button>
              </Grid>
            </Grid>
          )}
          {!error && (
            <>
              <Grid
                item
                container
                className={classes.headerContainer}
                spacing={1}
                justifyContent={'space-between'}
              >
                <AnalysisControlsHeader
                  updateDisabled={dateInputsDisabled}
                  loading={loading}
                  setIsTimeSelectorOpen={setIsTimeSelectorOpen}
                  start={start}
                  end={end}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  selectedQuickRange={selectedQuickRange}
                  site={site}
                  lastUpdated={lastUpdated}
                  downloading={downloading}
                  tagIds={tagIds}
                  onDownload={download}
                  triggeredOnly={triggeredOnly}
                  setTriggeredOnly={setTriggeredOnly}
                />
              </Grid>
              <Grid item container className={classes.chartsContainer}>
                <Grid item xs={12}>
                  <div className={classes.chartWrapper}>
                    {start && end && views.length ? (
                      views.map((view) =>
                        view.charts.map((chart) => (
                          <AnalysisChart
                            key={chart.id}
                            height={400}
                            tagIds={chart.tagsIds}
                            xRange={xRange}
                            snapshots={snapshots}
                            fullRangeSnapshots={fullRangeSnapshots}
                            isDrawerOpen={isTagsDrawerOpen}
                            setIsDrawerOpen={setIsTagsDrawerOpen}
                            viewId={view.id}
                            chart={chart}
                            fullRanges={fullRanges}
                            zoomRange={zoomRange}
                            setZoomRange={setZoomRange}
                            removeTagFromChartInView={removeTagFromChartInView}
                            removeChartInView={removeChartInView}
                            triggeredOnly={triggeredOnly}
                          />
                        ))
                      )
                    ) : (
                      <NoCharts setIsDrawerOpen={setIsTagsDrawerOpen} />
                    )}
                  </div>
                </Grid>
                {start && end && !!views.length && (
                  <Grid item className={classes.addChartButtonContainer}>
                    <SecondaryButton onClick={handleAddChart}>+ Add Chart</SecondaryButton>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
      <div className={classes.zoomControlContainer}>
        <ChartZoomControl
          fullRanges={zoomFullRanges}
          zoomRange={zoomRange}
          onZoomChange={setZoomRange}
          clearPosition="top"
          height={ZOOM_CONTROL_HEIGHT}
          backgroundColor="white"
        />
      </div>

      <TagsSelector
        views={views}
        isDrawerOpen={isTagsDrawerOpen}
        site={site}
        units={units}
        maxUnits={maxUnits}
        setIsDrawerOpen={setIsTagsDrawerOpen}
        updateViews={updateViews}
        updateTagsForChartInView={updateTagsForChartInView}
      />
      <TimeSelector
        isDrawerOpen={isTimeSelectorOpen}
        setIsDrawerOpen={setIsTimeSelectorOpen}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        start={start}
        end={end}
        loading={loading}
        selectedQuickRange={selectedQuickRange}
        setSelectedQuickRange={setSelectedQuickRange}
      />
    </div>
  );
};
